import { Link, useSearchParams } from 'react-router-dom'
import { UserButton, useAuth } from '@clerk/clerk-react'
import { useEffect, useMemo, useRef, useState } from 'react'

// Stripe redirects here after a successful Checkout. The subscription is
// provisioned asynchronously by the webhook, which can lag the redirect, so we
// poll /me until the plan flips off 'free' (or a timeout) before confirming.
export default function CheckoutSuccessPage() {
  const { getToken } = useAuth()
  const [params] = useSearchParams()
  const sessionId = params.get('session_id')
  const [status, setStatus] = useState<'pending' | 'active' | 'timeout'>('pending')
  const [plan, setPlan] = useState<string | null>(null)
  const stopped = useRef(false)

  const apiBase = useMemo(() => import.meta.env.VITE_API_URL || '/api/v1', [])

  useEffect(() => {
    const MAX_ATTEMPTS = 10 // ~20s at 2s intervals
    let attempts = 0

    const poll = async () => {
      if (stopped.current) return
      attempts += 1
      try {
        const token = await getToken()
        if (token) {
          const res = await fetch(`${apiBase}/me`, { headers: { Authorization: `Bearer ${token}` } })
          if (res.ok) {
            const { user } = await res.json()
            if (user?.plan && user.plan !== 'free') {
              setPlan(user.plan)
              setStatus('active')
              stopped.current = true
              return
            }
          }
        }
      } catch {
        /* transient — keep polling */
      }
      if (attempts >= MAX_ATTEMPTS) {
        setStatus('timeout')
        stopped.current = true
        return
      }
      setTimeout(poll, 2000)
    }

    poll()
    return () => {
      stopped.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-indigo-600">
              BankStatementReader
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="px-4 py-2 text-gray-700">Dashboard</Link>
              <UserButton />
            </div>
          </nav>
        </div>
      </header>

      <main className="container mx-auto px-4 py-16">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8 text-center">
          {status === 'pending' && (
            <>
              <div className="text-5xl mb-4">⏳</div>
              <h1 className="text-2xl font-bold text-gray-900">Finalizing your subscription…</h1>
              <p className="text-gray-600 mt-2">
                Payment received. We're activating your plan — this usually takes a few seconds.
              </p>
            </>
          )}

          {status === 'active' && (
            <>
              <div className="text-5xl mb-4">✅</div>
              <h1 className="text-2xl font-bold text-gray-900">You're on the {plan} plan!</h1>
              <p className="text-gray-600 mt-2">Your subscription is active. Time to upload some statements.</p>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div className="text-5xl mb-4">🕓</div>
              <h1 className="text-2xl font-bold text-gray-900">Payment received</h1>
              <p className="text-gray-600 mt-2">
                Your subscription is being processed and will appear shortly. Check your dashboard in a moment.
              </p>
            </>
          )}

          <Link
            to="/dashboard"
            className="inline-block mt-6 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Go to Dashboard
          </Link>

          {sessionId && (
            <p className="text-xs text-gray-400 mt-4 break-all">Ref: {sessionId}</p>
          )}
        </div>
      </main>
    </div>
  )
}
